import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { adminApi } from '../services/api';

const KYC_BADGE: Record<string, string> = {
  passed: 'bg-success',
  in_review: 'bg-info',
  pending: 'bg-warning text-dark',
  failed: 'bg-danger'
};

const KUNDKANNEDOM_LABEL_KEYS: Record<string, string> = {
  passed: 'adminAdyenAccounts.status.passed',
  in_review: 'adminAdyenAccounts.status.inReview',
  pending: 'adminAdyenAccounts.status.pending',
  failed: 'adminAdyenAccounts.status.failed'
};

export function AdminAdyenAccounts() {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'passed' | 'pending' | 'failed'>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    adminApi
      .getAdyenAccounts()
      .then(d => {
        setAccounts(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const filtered = accounts.filter(a => {
    if (filter !== 'all' && a.kycStatus !== filter) return false;
    if (search && !`${a.companyName || ''} ${a.adyenAccountHolderId || ''} ${a.adyenBalanceAccountId || ''}`
      .toLowerCase()
      .includes(search.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: accounts.length,
    passed: accounts.filter(a => a.kycStatus === 'passed').length,
    pending: accounts.filter(a => a.kycStatus === 'pending').length,
    failed: accounts.filter(a => a.kycStatus === 'failed').length
  };

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('adminAdyenAccounts.title')}</h5>
          </div>
          <Breadcrumb items={[{ label: t('adminAdyenAccounts.breadcrumb.home'), path: '/admin' }, { label: t('adminAdyenAccounts.breadcrumb.current') }]} />
        </div>
      </div>

      <div className="alert alert-info small mb-4">
        <i className="feather-info me-2"></i>
        <strong>{t('adminAdyenAccounts.mock.label')}</strong> {t('adminAdyenAccounts.mock.body')}
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card h-100">
            <div className="card-body p-3">
              <small className="text-muted">{t('adminAdyenAccounts.stats.total')}</small>
              <div className="fw-bold fs-4 text-primary">{stats.total}</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card h-100">
            <div className="card-body p-3">
              <small className="text-muted">{t('adminAdyenAccounts.stats.passed')}</small>
              <div className="fw-bold fs-4 text-success">{stats.passed}</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card h-100">
            <div className="card-body p-3">
              <small className="text-muted">{t('adminAdyenAccounts.stats.pending')}</small>
              <div className="fw-bold fs-4 text-warning">{stats.pending}</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card h-100">
            <div className="card-body p-3">
              <small className="text-muted">{t('adminAdyenAccounts.stats.failed')}</small>
              <div className="fw-bold fs-4 text-danger">{stats.failed}</div>
            </div>
          </div>
        </div>
      </div>

      <Card>
        <div className="d-flex flex-wrap gap-2 mb-3">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder={t('adminAdyenAccounts.search.placeholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
          <select className="form-select form-select-sm" value={filter} onChange={e => setFilter(e.target.value as any)} style={{ maxWidth: 200 }}>
            <option value="all">{t('adminAdyenAccounts.filters.all')}</option>
            <option value="passed">{t('adminAdyenAccounts.filters.passed')}</option>
            <option value="pending">{t('adminAdyenAccounts.filters.pending')}</option>
            <option value="failed">{t('adminAdyenAccounts.filters.failed')}</option>
          </select>
        </div>

        <div className="table-responsive">
          <table className="table table-sm mb-0">
            <thead className="table-light">
              <tr>
                <th>{t('adminAdyenAccounts.columns.company')}</th>
                <th>Account Holder</th>
                <th>Balance Account</th>
                <th>{t('adminAdyenAccounts.columns.kyc')}</th>
                <th>{t('adminAdyenAccounts.columns.payout')}</th>
                <th>{t('adminAdyenAccounts.columns.bankAccount')}</th>
                <th className="text-end">{t('adminAdyenAccounts.columns.balance')}</th>
                <th>Onboarding</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="text-center py-4">
                    <div className="spinner-border spinner-border-sm text-primary" />
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">{t('adminAdyenAccounts.empty')}</td>
                </tr>
              )}
              {filtered.map(a => (
                <tr key={a.id}>
                  <td>
                    <div className="fw-semibold">{a.companyName || '—'}</div>
                    <small className="text-muted">#{a.companyNumber}</small>
                  </td>
                  <td className="font-monospace small">{a.adyenAccountHolderId}</td>
                  <td className="font-monospace small">{a.adyenBalanceAccountId}</td>
                  <td>
                    <span className={`badge ${KYC_BADGE[a.kycStatus] || 'bg-secondary'}`}>
                      {KUNDKANNEDOM_LABEL_KEYS[a.kycStatus] ? t(KUNDKANNEDOM_LABEL_KEYS[a.kycStatus]) : a.kycStatus}
                    </span>
                  </td>
                  <td>
                    {a.payoutsAllowed ? (
                      <span className="text-success small">
                        <i className="feather-check me-1"></i>{t('adminAdyenAccounts.payout.allowed')}
                      </span>
                    ) : (
                      <span className="text-muted small">
                        <i className="feather-lock me-1"></i>{t('adminAdyenAccounts.payout.blocked')}
                      </span>
                    )}
                  </td>
                  <td className="font-monospace small text-muted">{a.bankAccountMasked}</td>
                  <td className="text-end fw-semibold">{new Intl.NumberFormat('sv-SE').format(a.balanceSek || 0)} {t('adminAdyenAccounts.balanceSuffix')}</td>
                  <td>
                    {a.onboardingCompletedAt ? (
                      <small className="text-muted">{new Date(a.onboardingCompletedAt).toLocaleDateString('sv-SE')}</small>
                    ) : a.onboardingUrl ? (
                      <a href={a.onboardingUrl} target="_blank" rel="noopener noreferrer" className="small">
                        {t('adminAdyenAccounts.onboarding.start')}
                        <i className="feather-external-link ms-1" style={{ fontSize: 11 }}></i>
                      </a>
                    ) : (
                      <small className="text-muted">—</small>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </DashboardLayout>
  );
}
